import {basePortalApi as api} from "../basePortalApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    attachmentFetchAttachments: build.mutation<
      AttachmentFetchAttachmentsApiResponse,
      AttachmentFetchAttachmentsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/organizations/${queryArg.organizationId}/attachments/fetchAttachments`,
        method: "POST",
        body: queryArg.fetchAttachmentsCommandValues,
      }),
    }),
    attachmentGetAttachment: build.mutation<
      AttachmentGetAttachmentApiResponse,
      AttachmentGetAttachmentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/organizations/${queryArg.organizationId}/attachments/${queryArg.attachmentId}`,
      }),
    }),
  }),
  overrideExisting: false,
});
export {injectedRtkApi as portalApi};
export type AttachmentFetchAttachmentsApiResponse =
  /** status 200  */ AttachmentDtoPagedResult;
export type AttachmentFetchAttachmentsApiArg = {
  organizationId: number;
  fetchAttachmentsCommandValues: FetchAttachmentsCommandValues;
};
export type AttachmentGetAttachmentApiResponse =
  /** status 200  */ AttachmentDto;
export type AttachmentGetAttachmentApiArg = {
  organizationId: number;
  attachmentId: number;
};
export type AttachmentType = "Picture" | "OtherDocument";
export type AttachmentParentType =
  | "Order"
  | "Contact"
  | "AccountingTransaction"
  | "EquipmentType";
export type LinkDto = {
  href?: string | null;
  rel?: string | null;
  type?: string | null;
  templated?: boolean | null;
};
export type AttachmentDto = {
  attachmentId?: number;
  attachmentType?: AttachmentType;
  created?: string;
  createdBy?: string | null;
  description?: string | null;
  fileName?: string | null;
  fileUri?: string | null;
  previewUri?: string | null;
  thumbnailUri?: string | null;
  lastModified?: string;
  lastModifiedBy?: string | null;
  organizationId?: number;
  parentId?: number;
  parentType?: AttachmentParentType;
  createdByUserName?: string | null;
  lastModifiedByUserName?: string | null;
  presignedFileUri?: string | null;
  presignedPreviewUri?: string | null;
  presignedThumbnailUri?: string | null;
  links?: LinkDto[] | null;
};
export type AttachmentDtoPagedResult = {
  offset?: number;
  limit?: number;
  totalCount?: number;
  items?: AttachmentDto[] | null;
  links?: LinkDto[] | null;
};
export type ValidationProblemDetailsResponse = {
  errors?: {
    [key: string]: string[];
  } | null;
  type?: string | null;
  title?: string | null;
  status?: number | null;
  detail?: string | null;
  instance?: string | null;
  [key: string]: any | null;
};
export type FetchAttachmentsCommandValues = {
  orderIds?: number[];
};
export enum S3UriTypes {
  FileUri = "FileUri",
  PreviewUri = "PreviewUri",
  ThumbnailUri = "ThumbnailUri",
}
export const {
  useAttachmentFetchAttachmentsMutation,
  useAttachmentGetAttachmentMutation,
} = injectedRtkApi;

/**
 * Gets the best available presigned URI for an attachment.
 * Only returns a presigned URI if the underlying file URI exists.
 * Priority: thumbnail -> preview -> file
 */
export const getAttachmentPresignedUri = (
  attachment: AttachmentDto | null | undefined,
): string | null => {
  if (!attachment) return null;

  if (attachment.thumbnailUri && attachment.presignedThumbnailUri) {
    return attachment.presignedThumbnailUri;
  }
  if (attachment.previewUri && attachment.presignedPreviewUri) {
    return attachment.presignedPreviewUri;
  }
  if (attachment.fileUri && attachment.presignedFileUri) {
    return attachment.presignedFileUri;
  }
  return null;
};

/**
 * Gets all presigned URIs for an attachment, checking if underlying URIs exist.
 * Used by ImageWithModal to get thumbnail, preview, and file URIs separately.
 */
export const getAttachmentPresignedUris = (
  attachment: AttachmentDto | null | undefined,
): {
  thumbnailUri: string;
  previewUri: string;
  fileUri: string;
} => {
  if (!attachment) {
    return {thumbnailUri: "", previewUri: "", fileUri: ""};
  }

  const fileUri = attachment.fileUri ? attachment.presignedFileUri ?? "" : "";
  const previewUri = attachment.previewUri
    ? attachment.presignedPreviewUri ?? fileUri
    : fileUri;
  const thumbnailUri = attachment.thumbnailUri
    ? attachment.presignedThumbnailUri ?? previewUri
    : previewUri;

  return {thumbnailUri, previewUri, fileUri};
};
